/**
 * Number format types
 */
export enum NumberType {
  CURRENCY = "CURRENCY",
  CURRENCY_WITH_DECIMAL = "CURRENCY_WITH_DECIMAL",
  CURRENCY_WS = "CURRENCY_WS",
  CURRENCY_K = "CURRENCY_K",
  CURRENCY_K_WS = "CURRENCY_K_WS",
  CURRENCY_K_SYMBOL = "CURRENCY_K_SYMBOL",
  CURRENCY_K_SYMBOL_WS = "CURRENCY_K_SYMBOL_WS",
  CURRENCY_NO_CENTS = "CURRENCY_NO_CENTS",
  CURRENCY_NO_CENTS_WS = "CURRENCY_NO_CENTS_WS",
  CURRENCY_K_NO_CENTS_WS = "CURRENCY_K_NO_CENTS_WS",
  CURRENCY_SHORT_NO_CENTS = "CURRENCY_SHORT_NO_CENTS",
  CURRENCY_SHORT_NO_CENTS_WS = "CURRENCY_SHORT_NO_CENTS_WS",
  CURRENCY_SHORT_CENTS_OPTIONAL_DECIMALS = "CURRENCY_SHORT_CENTS_OPTIONAL_DECIMALS",
  CURRENCY_SHORT_CENTS_OPTIONAL_DECIMALS_WS = "CURRENCY_SHORT_CENTS_OPTIONAL_DECIMALS_WS",
  PERCENTAGE = "PERCENTAGE",
  PERCENTAGE_WS = "PERCENTAGE_WS",
  PERCENTAGE_OPTIONAL_DECIMALS = "PERCENTAGE_OPTIONAL_DECIMALS",
  K = "K",
  TIME = "TIME",
  INTEGER = "INTEGER",
  INTEGER_WS = "INTEGER_WS",
  NUMBER_COMMA = "NUMBER_COMMA",
}

interface NumberFormatOptions extends Intl.NumberFormatOptions {
  trailingZeroDisplay?: "auto" | "stripIfInteger";
}

export const numberFormats: Record<NumberType, NumberFormatOptions> = {
  [NumberType.CURRENCY]: {
    style: "currency",
    trailingZeroDisplay: "stripIfInteger",
    currencyDisplay: "narrowSymbol",
  },
  [NumberType.CURRENCY_SHORT_NO_CENTS]: {
    style: "currency",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  },
  [NumberType.CURRENCY_SHORT_NO_CENTS_WS]: {
    style: "currency",
    signDisplay: "always",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  },
  [NumberType.CURRENCY_SHORT_CENTS_OPTIONAL_DECIMALS]: {
    style: "currency",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    trailingZeroDisplay: "stripIfInteger",
  },
  [NumberType.CURRENCY_SHORT_CENTS_OPTIONAL_DECIMALS_WS]: {
    style: "currency",
    signDisplay: "always",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  },
  [NumberType.CURRENCY_WITH_DECIMAL]: {
    style: "currency",
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
  [NumberType.CURRENCY_WS]: {
    style: "currency",
    trailingZeroDisplay: "stripIfInteger",
    signDisplay: "always",
    currencyDisplay: "narrowSymbol",
  },
  [NumberType.CURRENCY_K]: {
    style: "currency",
    notation: "compact",
    currencyDisplay: "narrowSymbol",
  },
  [NumberType.CURRENCY_K_WS]: {
    style: "currency",
    signDisplay: "always",
    notation: "compact",
    currencyDisplay: "narrowSymbol",
  },
  [NumberType.CURRENCY_K_SYMBOL]: {
    style: "currency",
    notation: "compact",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  },
  [NumberType.CURRENCY_K_SYMBOL_WS]: {
    style: "currency",
    notation: "compact",
    signDisplay: "always",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  },
  [NumberType.CURRENCY_NO_CENTS]: {
    style: "currency",
    maximumFractionDigits: 0,
    currencyDisplay: "narrowSymbol",
  },
  [NumberType.CURRENCY_NO_CENTS_WS]: {
    style: "currency",
    maximumFractionDigits: 0,
    signDisplay: "always",
    currencyDisplay: "narrowSymbol",
  },
  [NumberType.CURRENCY_K_NO_CENTS_WS]: {
    style: "currency",
    maximumFractionDigits: 0,
    signDisplay: "always",
    notation: "compact",
    currencyDisplay: "narrowSymbol",
  },
  [NumberType.PERCENTAGE]: { style: "percent", useGrouping: false },
  // Same as PERCENTAGE but renders 0–2 decimal places only when needed.
  // e.g. 0.13 → "13%", 0.115 → "11.5%", 0.1125 → "11.25%". Use for
  // commission rates and other values where fractional precision matters.
  [NumberType.PERCENTAGE_OPTIONAL_DECIMALS]: {
    style: "percent",
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  },
  [NumberType.PERCENTAGE_WS]: {
    style: "percent",
    signDisplay: "always",
    useGrouping: false,
  },
  [NumberType.K]: { notation: "compact" },
  [NumberType.INTEGER]: { maximumFractionDigits: 0, useGrouping: false },
  [NumberType.INTEGER_WS]: {
    maximumFractionDigits: 0,
    signDisplay: "always",
    useGrouping: false,
  },
  [NumberType.TIME]: {},
  [NumberType.NUMBER_COMMA]: {},
};

/**
 * Format time in seconds to HH:MM:SS format
 * @param time time in seconds
 * @returns formatted time in HH:MM:SS format
 */
export const formatTime = (time: number) => {
  const hours = Math.abs(Math.floor(time / 3600));
  const minutes = Math.abs(Math.floor((time % 3600) / 60));
  const seconds = Math.abs(Math.floor(time % 60));

  const formattedTime = `${time < 0 ? `-` : ""}${String(hours).padStart(
    2,
    "0"
  )}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

  return formattedTime;
};

/**
 *
 * @param value number to format.
 * @param type format type. Default is currency
 * @param currency currency code (ISO 4217) must be supplied if format type is currency. If null or invalid string is supplied, default is USD.
 * @param locale locale code ex. en-US (ISO 639-1)-(ISO 3166-1). If undefined, null or empty string, browser default is used.
 * @returns formatted number as string
 */
export const numberFormatter = (
  value: number,
  type: NumberType = NumberType.CURRENCY,
  currencyCode?: string | null,
  locale?: string | string[] | null
) => {
  if (type === NumberType.TIME) {
    return formatTime(value);
  }
  let valueToFormat = value;
  let formattedNumber: string;
  let formatOptions: Intl.NumberFormatOptions;
  // if locale is an empty string, Intl.NumberFormat will throw an error so we need to pass undefined to default to the system default locale
  const localeCode = locale ?? undefined;
  const currency = currencyCode ?? "USD";
  if (
    value === Infinity ||
    (value < 0 && value > -5e-5) ||
    (value > 0 && value < 1e-6)
  ) {
    valueToFormat = 0;
  }
  try {
    formatOptions = { ...numberFormats[type], currency };
    formattedNumber = new Intl.NumberFormat(localeCode, formatOptions).format(
      valueToFormat
    );
  } catch (e) {
    console.error(e);
    // if Intl.NumberFormat throws an error, default to USD as the currency
    formatOptions = { ...numberFormats[type], currency: "USD" };
    formattedNumber = new Intl.NumberFormat(undefined, formatOptions).format(
      valueToFormat
    );
  }
  return formattedNumber;
};
